import React, {useEffect, useRef, useState, createRef} from "react";
import {useTranslation} from "react-i18next";
import Checkbox from "../lib/form/Checkbox/Checkbox";

export type SelectOptionType = {text: string | React.ReactNode; value: string | number | undefined};

type SelectProps = {
  value?: string | number | (string | number)[];
  onChange?: (value: string | number | (string | number)[] | undefined) => void;
  placeHolder?: string | React.ReactNode;
  options: SelectOptionType[];
  onClear?: () => void;
  title?: string;
  multi?: boolean;
  styles?: SelectClasses;
  openIcon?: string;
  closeIcon?: string;
};

type SelectClasses = {
  wrapper?: string;
  optionsWrapper?: string;
  selected?: string;
  option?: string;
  placeholder?: string;
};

const Select: React.FC<SelectProps> = ({
  onChange = () => {
    return;
  },
  onClear,
  placeHolder,
  options,
  value,
  title = "",
  multi = false,
  openIcon = "icon-chevron-down icon-size--16",
  closeIcon = "icon-chevron-up icon-size--16",
  styles = {
    wrapper: "",
    optionsWrapper: "",
    option: "",
    selected: "",
    placeholder: "",
  },
}) => {
  const {t} = useTranslation();

  const [selectedOptions, setSelectedOptions] = useState<SelectOptionType[]>([]);
  const [opened, setOpened] = useState(false);
  const [markedValue, setMarkedValue] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const optionsRefs = useRef(Array.from({length: options.length}, () => createRef<HTMLDivElement>()));
  const openRef = useRef(false);
  const searchWord = useRef("");
  const last = useRef(new Date().getTime());

  useEffect(() => {
    const handleClick = (evt: MouseEvent) => {
      if (!openRef.current) {
        return;
      }
      if (!(containerRef.current === evt.target || containerRef.current?.contains(evt.target as HTMLElement))) {
        setOpened(false);
        openRef.current = false;
        setMarkedValue("");
      }
    };

    document.body.addEventListener("click", handleClick);

    return () => {
      document.body.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedOptions(options.filter((option) => value.some((_value) => _value === option.value)));
    } else {
      const option = options.find((_option) => _option.value === value);

      setSelectedOptions(option ? [option] : []);
    }
  }, [value]);

  // option click
  const onSelect = (option: SelectOptionType) => {
    onChange(option.value);
    if (!multi) {
      setOpened(false);
      openRef.current = false;
      setMarkedValue("");
    }
  };

  const handleKeypressEvent = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (openRef.current) {
      const now = new Date().getTime();
      if (now - last.current < 2000) {
        searchWord.current += event.key;
      } else {
        searchWord.current = event.key;
        setMarkedValue("");
      }

      last.current = now;

      const optionIndex = optionsRefs.current.findIndex((_option) =>
        _option.current?.innerText.toLowerCase().startsWith(searchWord.current.toLowerCase()),
      );
      if (optionIndex > -1) {
        const option = optionsRefs.current[optionIndex].current;
        if (option) {
          optionsContainerRef.current?.scrollTo({
            top: option.offsetTop - optionsContainerRef.current.offsetHeight / 2 + option.offsetHeight,
          });
          setMarkedValue(String(options[optionIndex].value));
        }
      } else {
        const indexes: number[] = [];
        optionsRefs.current.forEach((_option, index) => {
          if (
            String(_option.current?.innerText)
              .toLowerCase()
              .startsWith(searchWord.current[searchWord.current.length - 1].toLowerCase())
          ) {
            indexes.push(index);
          }
        });

        if (indexes.length) {
          const index =
            searchWord.current.length % indexes.length === 0
              ? indexes.length - 1
              : (searchWord.current.length % indexes.length) - 1;
          const option = optionsRefs.current[indexes[index]].current;
          if (option) {
            optionsContainerRef.current?.scrollTo({
              top: option.offsetTop - optionsContainerRef.current.offsetHeight / 2 + option.offsetHeight,
            });
            setMarkedValue(String(options[indexes[index]].value));
          }
        }
      }
    }
  };

  // is there an option, that can be cleared by 'onClear'
  const isSelectedOptionToClear = onClear && selectedOptions;
  const isOneOrLessOption = options.length <= 1;

  // styles handling design and behaviour
  const containerClassName = [
    "dd__wrapper",
    styles.wrapper,
    isSelectedOptionToClear || isOneOrLessOption ? "dd--unselectable" : "",
  ].join(" ");

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      onKeyPress={handleKeypressEvent}
      tabIndex={0}
      onClick={() => {
        if (isSelectedOptionToClear) {
          return;
        }
        setOpened(!opened);
        openRef.current = !opened;
        setMarkedValue("");
      }}
    >
      {selectedOptions.length ? (
        <>
          <span className="dd__title">
            {title} {multi ? "" : selectedOptions[0].text}
          </span>
          <i className={`icon ${opened ? closeIcon : openIcon}`} />
        </>
      ) : (
        <span className={[styles.placeholder, "dd--placeholder"].join(" ")}>
          {placeHolder ?? t("form.pleaseChoose")}
        </span>
      )}
      {opened && options.length > 0 && (
        <div ref={optionsContainerRef} className={`dd__content ${styles.optionsWrapper ?? ""}`}>
          {options.map((option, index) => (
            <div
              ref={optionsRefs.current[index]}
              key={index}
              className={`dd__option${markedValue === String(option.value) ? " " + "marked" : ""}${
                multi ? " " + "multi" : ""
              } ${styles.option ?? ""} ${
                selectedOptions.some((_option) => _option.value === option.value)
                  ? ["dd__option--selected", styles.selected].join(" ")
                  : ""
              }`}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {multi ? (
                <Checkbox
                  wrapperClassName="checkbox"
                  checked={selectedOptions.some((_option) => _option.value === option.value)}
                  color={"green"}
                  onChange={() => {
                    onSelect(option);
                  }}
                >
                  {option.text}
                </Checkbox>
              ) : (
                <div
                  onClick={() => {
                    onSelect(option);
                  }}
                >
                  {option.text}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Select;
